import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PublicHeader from "@/components/layout/PublicHeader";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageSEO } from "@/hooks/use-seo";

const TermsOfService = () => {
  const navigate = useNavigate();

  usePageSEO(
    "Terms of Service - Zoocertica",
    "Zoocertica Terms of Service governing use of our wildlife management platform for zoos, sanctuaries, and private collectors.",
    "/terms"
  );

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Zoocertica Terms of Service</CardTitle>
            <p className="text-sm text-muted-foreground">
              Updated October 2025
            </p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none dark:prose-invert space-y-6">
            <p>
              Welcome to Zoocertica ("we," "our," "us"). These Terms govern your use of all Zoocertica websites, applications, APIs, and related services ("Service"). By creating an account or using the Service, you agree to these Terms.
            </p>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Eligibility & Accounts</h2>
              <p>
                You must be 18 years or older (or legal age in your jurisdiction). You are responsible for all activity under your account and for keeping credentials secure. Information supplied during registration must remain accurate and current. You are solely responsible for ensuring your use of Zoocertica complies with all applicable wildlife, animal-care, transport, and data-protection laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Service Overview</h2>
              <p>Zoocertica provides professional and private-keeper management tools, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Animal, enclosure, and medical recordkeeping</li>
                <li>Compliance, permit, and transport documentation</li>
                <li>Legality lookup and transfer certification</li>
                <li>Breeding, environmental, and growth tracking</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Subscription Plans & Billing</h2>
              <p>
                Zoocertica operates on paid subscriptions managed through Stripe. Plans include Zoo/Sanctuary Plans, Collector Plans, and Collector Add-Ons (50% discount for Zoo users). Subscriptions renew automatically unless canceled before renewal. Usage limits, pricing, and plan details are posted at{" "}
                <a href="https://zoocertica.com/pricing" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                  https://zoocertica.com/pricing
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Trials, Upgrades & Refunds</h2>
              <p>
                Free trials may be offered at our discretion. Upgrades apply immediately; downgrades take effect at the next billing cycle. Refunds available within 14 days if no transfer or export generated.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Acceptable Use</h2>
              <p>
                Users must not upload illegal wildlife data or violate regulations. No reverse engineering or system abuse is permitted. Accounts may be suspended for violations.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Data Ownership & Responsibility</h2>
              <p>
                Users retain ownership of uploaded data. Zoocertica may store and process data solely to operate the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Intellectual Property</h2>
              <p>
                All code, designs, and databases remain Zoocertica property. No reselling or sublicensing without consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Service Availability</h2>
              <p>
                We target 99.9% uptime; details in the SLA. Maintenance and external outages are excluded.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Compliance Disclaimer</h2>
              <p>
                Zoocertica assists with documentation but does not provide legal advice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Limitation of Liability</h2>
              <p>
                Zoocertica not liable for indirect losses or enforcement actions. Liability capped at 12 months of paid fees.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
              <p>
                We may suspend or terminate accounts for violations. Users can terminate anytime via dashboard.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Governing Law</h2>
              <p>
                Governed by South Carolina, USA.
              </p>
            </section>

            <div className="mt-8 p-4 bg-muted rounded-lg border">
              <p className="text-sm font-semibold text-center">
                BY USING THIS SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
